import type { inferProcedureInput, inferProcedureOutput } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'

type InitialData = inferProcedureOutput<AppRouter['admin']['userDashboard']['init']>
type EvaluationHeaderConfig = InitialData['evaluationHeaderConfig']
type Organization = InitialData['organization']
type AvailableFields = InitialData['availableFields']
export type User = InitialData['users']['data'][number]
export type SearchQuery = inferProcedureInput<AppRouter['admin']['userDashboard']['search']>

type FieldConfig = {
	id?: string
	name: string
	sources: Array<{ type: 'customFields' | 'hierarchy' | 'user'; key: string }>
	order: number
	formId?: string | null
}

export interface UserDashboardStore {
	users: InitialData['users']
	organization: Organization | null
	availableFields: AvailableFields | null
	evaluationHeaderConfig: EvaluationHeaderConfig | null
	actions: string[]

	// UI State
	isLoading: boolean
	error: string | null
	searchQuery: SearchQuery
	selectedUsers: User[]
	isExporting: boolean

	// Actions
	initialize: (organizationId: string) => Promise<void>
	searchUsers: () => Promise<void>
	setSearchQuery: (value?: Omit<SearchQuery, 'organizationId'>) => void
	setSelectedUsers: (userIds?: User[]) => void
	archiveSelectedUsers: () => Promise<void>
	deactivateSelectedUsers: () => Promise<void>
	activateSelectedUsers: () => Promise<void>
	exportUsers: (includeInactive?: boolean) => Promise<boolean>
	getEvaluationHeaderConfig: () => Promise<void>
	updateEvaluationHeaderConfig: (config: FieldConfig[]) => Promise<void>
	setError: (error?: string | null) => void
}

export type UserDashboardStoreInstance = ReturnType<typeof createUserDashboardStore>

export const createUserDashboardStore = () => {
	return create<UserDashboardStore>()(
		devtools(
			(set, get) => ({
				// Initial state
				users: { data: [], meta: { page: 1, perPage: 25, total: 0, totalInPage: 0 } },
				organization: null,
				availableFields: null,
				evaluationHeaderConfig: null,
				actions: [],
				isLoading: false,
				isExporting: false,
				error: null,
				searchQuery: '',
				selectedUsers: [],

				// Actions
				initialize: async (organizationId) => {
					set({ isLoading: true, error: null })
					try {
						const data = await trpcClient.admin.userDashboard.init.query({
							organizationId,
						})
						set({
							users: data.users,
							organization: data.organization,
							searchQuery: {
								organizationId,
							},
							availableFields: data.availableFields,
							evaluationHeaderConfig: data.evaluationHeaderConfig,
							actions: data.actions,
							isLoading: false,
						})
					} catch (error) {
						console.error('Failed to initialize user dashboard:', error)
						set({
							error: error instanceof Error ? error.message : 'Failed to load user dashboard',
							isLoading: false,
						})
					}
				},

				searchUsers: async () => {
					set({ error: null })
					const { organization, searchQuery } = get()
					if (!organization) {
						throw new Error('No organization available')
					}

					try {
						const data = await trpcClient.admin.userDashboard.search.query({
							...searchQuery,
						})
						set({
							users: data,
						})
					} catch (error) {
						console.error('Failed to search users:', error)
						set({
							error: error instanceof Error ? error.message : 'Failed to search users',
						})
					}
				},

				async archiveSelectedUsers() {
					const { organization, selectedUsers } = get()

					if (!organization) {
						return set({
							error: 'Missing organization',
						})
					}

					try {
						const users = await trpcClient.admin.userDashboard.archiveUsers.mutate({
							organizationId: organization.id,
							userIds: selectedUsers.map(({ id }) => id),
						})

						set({
							selectedUsers: [],
							users,
						})
					} catch (error) {
						set({
							error: error instanceof Error ? error.message : 'Unknown error archiving users',
						})
					}
				},

				async activateSelectedUsers() {
					const { organization, selectedUsers } = get()

					if (!organization) {
						return set({
							error: 'Missing organization',
						})
					}

					try {
						const users = await trpcClient.admin.userDashboard.toggleUserActive.mutate({
							organizationId: organization.id,
							userIds: selectedUsers.map(({ id }) => id),
							active: true,
						})

						set({
							selectedUsers: [],
							users,
						})
					} catch (error) {
						set({
							error: error instanceof Error ? error.message : 'Unknown error activating users',
						})
					}
				},

				async deactivateSelectedUsers() {
					const { organization, selectedUsers } = get()

					if (!organization) {
						return set({
							error: 'Missing organization',
						})
					}

					try {
						const users = await trpcClient.admin.userDashboard.toggleUserActive.mutate({
							organizationId: organization.id,
							userIds: selectedUsers.map(({ id }) => id),
							active: false,
						})

						set({
							selectedUsers: [],
							users,
						})
					} catch (error) {
						set({
							error: error instanceof Error ? error.message : 'Unknown error deactivating users',
						})
					}
				},

				async exportUsers(includeInactive = true) {
					const { organization, isExporting } = get()
					if (!organization || isExporting) return false

					set({ isExporting: true })
					try {
						const result = await trpcClient.admin.userDashboard.exportUsers.mutate({
							organizationId: organization.id,
							includeInactive,
						})
						// Anchor click avoids popup-blocker rejection that hits window.open after an awaited
						// tRPC call (user-gesture context expires). The presigned URL embeds
						// Content-Disposition: attachment, so the browser downloads instead of navigating.
						const link = Object.assign(document.createElement('a'), {
							href: result.url,
							download: result.filename,
						})
						link.click()
						return true
					} catch {
						return false
					} finally {
						set({ isExporting: false })
					}
				},

				getEvaluationHeaderConfig: async () => {
					const { organization } = get()
					if (!organization) {
						throw new Error('No organization available')
					}

					try {
						const config = await trpcClient.admin.userDashboard.getEvaluationHeaderConfig.query({
							organizationId: organization.id,
						})
						set({ evaluationHeaderConfig: config })
					} catch (error) {
						console.error('Failed to get evaluation header config:', error)
						set({
							error: error instanceof Error ? error.message : 'Failed to get evaluation header config',
						})
					}
				},

				updateEvaluationHeaderConfig: async (config) => {
					const { organization } = get()
					if (!organization) {
						throw new Error('No organization available')
					}

					try {
						const updatedConfig = await trpcClient.admin.userDashboard.updateEvaluationHeaderConfig.mutate({
							organizationId: organization.id,
							config,
						})
						set({ evaluationHeaderConfig: updatedConfig })
					} catch (error) {
						console.error('Failed to update evaluation header config:', error)
						set({
							error: error instanceof Error ? error.message : 'Failed to update evaluation header config',
						})
						throw error
					}
				},

				setSearchQuery: (value) => {
					set(({ searchQuery }) => ({
						searchQuery: {
							...searchQuery,
							...value,
						},
					}))
				},

				setSelectedUsers(users) {
					set({ selectedUsers: users })
				},

				setError: (error) => set({ error }),
			}),
			{ name: 'user-dashboard-store' },
		),
	)
}
